import { Button, Stack, Typography } from "@mui/joy";
import { PlanKey } from "../task/payment/planTypes";
import { theme } from "../../styles/theme";
import useIsInappWebview from "../../hooks/useIsInappWebview";
import { useEffect, useState } from "react";
import { canUpgradePlan } from "../../utils/planChangeUtils";
import { useRecoilValue } from "recoil";
import { userAtom } from "../../recoil/userAtom";
import { taskListAtom } from "../../recoil/taskListAtom";
import { homeThoughtRecordListAtom } from "../../recoil/homeThoughtRecordListAtom";
import useOpenPlanUpgrade from "../../hooks/useOpenPlanUpgrade";

export default function PlanUpgradeModule({
  currentPlan,
}: {
  currentPlan: PlanKey;
}) {
  const user = useRecoilValue(userAtom);
  const openPlanUpgradePage = useOpenPlanUpgrade({
    isFromHome: true,
  });
  const isInappWebview = useIsInappWebview();
  const [show, setShow] = useState<boolean>(false);

  const taskListState = useRecoilValue(taskListAtom);
  const homeThoughtRecordList = useRecoilValue(homeThoughtRecordListAtom);

  useEffect(() => {}, [homeThoughtRecordList, taskListState]);

  const isPlanUpgradeBlackoutTime = () => {
    const now = new Date();
    const day = now.getDay(); // 0 (Sun) - 6 (Sat)
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const seconds = now.getSeconds();
    if (day === 5) {
      // Friday after 14:00:00 strictly (i.e., 14:00:01 and later)
      return hours > 14 || (hours === 14 && (minutes > 0 || seconds > 0));
    }
    if (day === 6 || day === 0) {
      return true; // Saturday or Sunday
    }
    return false; // Monday-Thursday, and Friday up to and including 14:00:00
  };

  useEffect(() => {
    if (user?.plan === "master") {
      setShow(false);
      return;
    }

    if (user?.plan === "starter" && (user?.weeklyTaskUpdateCount || 0) < 2) {
      setShow(false);
      return;
    }

    if (user?.plan === "challenger" && (user?.todayTaskUpdateCount || 0) < 1) {
      setShow(false);
      return;
    }

    if (user?.pricingModel !== "ajk" && user?.pricingModel !== "aeg") {
      setShow(false);
      return;
    }

    const isPlanUpgradeButtonShown =
      canUpgradePlan(user) && !isPlanUpgradeBlackoutTime();

    if (isPlanUpgradeButtonShown) {
      if (taskListState.length > 0 || homeThoughtRecordList.length > 0) {
        const todoTaskList = taskListState.filter(
          (element) => element.isOpen && !element.isOptional
        );
        const doneTaskList = taskListState.filter(
          (element) => element.isOpen && element.isDone && !element.isOptional
        );

        const todoThoughtRecordList = homeThoughtRecordList.filter(
          (element) => element.isOpen
        );
        const doneThoughtRecordList = homeThoughtRecordList.filter(
          (element) => element.isOpen && element.isDone
        );

        const todoCount = todoTaskList.length + todoThoughtRecordList.length;
        const doneCount = doneTaskList.length + doneThoughtRecordList.length;

        if (todoCount > 0 && todoCount === doneCount) {
          setShow(true);
        }
      }
    }
  }, [user, taskListState, homeThoughtRecordList]);

  const text = {
    starter: {
      title: "매일 받는 업데이트",
      subtitle:
        "마스터와 챌린저 플랜은 주 2회 제한 없이 매일 업데이트를 받아볼 수 있습니다.",
    },
    challenger: {
      title: `한 번 더 받는 업데이트`,
      subtitle: `마스터 플랜은 일 최대 2회까지 업데이트를 받아볼 수 있습니다.`,
    },
  };

  if (!show || currentPlan === "master" || isPlanUpgradeBlackoutTime()) {
    return null;
  }

  return (
    <Stack
      sx={{
        width: "100%",
        backgroundColor: "white",
      }}
    >
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        spacing="32px"
        alignItems="center"
        sx={{
          width: "100%",
          maxWidth: "660px",
          mx: "auto",
          px: "24px",
          py: "16px",
        }}
      >
        <Stack spacing={"2px"}>
          <Typography
            variant="Body"
            sx={{
              color: theme.vars.palette.Content.Strong,
              fontWeight: 700,
              pr: 0,
            }}
          >
            {text[currentPlan].title}
          </Typography>
          <Typography
            variant="Caption"
            sx={{
              color: theme.vars.palette.Content.Weak,
              fontWeight: 500,
              pr: 0,
            }}
          >
            {text[currentPlan].subtitle}
          </Typography>
        </Stack>
        <Button
          size="sm"
          onClick={() => {
            openPlanUpgradePage();
          }}
          sx={{
            whiteSpace: "nowrap",
            minWidth: "auto",
          }}
        >
          {"플랜 변경"}
        </Button>
      </Stack>
    </Stack>
  );
}
